import { useEffect, useState } from "react";

import isNil from "lodash/isNil";

import { BookingAnalyticsEvent } from "@/constants/analytics/Analytics";
import { ConfirmationAlertTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useBookingAnalytics from "@/hooks/useBookingAnalytics";
import { useAppSelector } from "@/store/hooks";
import { selectBookingStep, selectInitialStep } from "@/store/reducers/booking";
import BookingStepMap from "@/ui/components/schedule/booking/BookingStepMap";
import ConfirmationAlertModal from "@/ui/composed/alerts/ConfirmationAlert";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import { FAIconName } from "@/ui/core";
import ModalLayout from "@/ui/layout/ModalLayout";

const dismissModal = () => {
  router.dismiss();
};

const Modal = () => {
  const currentStep = useAppSelector(selectBookingStep);
  const initialStep = useAppSelector(selectInitialStep);

  const StepComponent = BookingStepMap[currentStep || initialStep];
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const { trackAlertEvent, trackAlertView, trackEvent } = useBookingAnalytics();
  const isInitialStep = isNil(currentStep);

  const alertType: BookingAlertType = "dismiss";

  useEffect(() => {
    if (isAlertOpen) {
      trackAlertView(alertType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAlertOpen]);

  const confirmBookingDismiss = () => {
    setIsAlertOpen(true);
  };

  const handleDismiss = () => {
    trackEvent({
      event: BookingAnalyticsEvent.Close,
      source: currentStep || initialStep,
    });

    if (isInitialStep) {
      dismissModal();
      return;
    }
    confirmBookingDismiss();
  };

  const handleAlertDismiss = () => {
    setIsAlertOpen(false);
    trackAlertEvent({
      event: BookingAnalyticsEvent.Close,
      source: alertType,
    });
  };

  const handleAlertConfirm = () => {
    dismissModal();
    trackAlertEvent({
      event: BookingAnalyticsEvent.LeaveBookingModal,
      source: alertType,
    });
  };

  const modalTitle = "Book an appointment";

  return (
    <>
      <ModalLayout
        isListView
        renderHeader={() => {
          return (
            <ModalTitleHeader
              navigationType="close"
              onDismiss={handleDismiss}
              testID="book-appointment"
              title={modalTitle}
            />
          );
        }}
      >
        {StepComponent}
      </ModalLayout>
      <ConfirmationAlertModal
        confirmationType="destructive"
        confirmLeftIcon={FAIconName.ArrowRightFromBracket}
        confirmText="Leave booking"
        isOpen={isAlertOpen}
        message="If you leave now, your booking request will not be completed."
        onConfirm={handleAlertConfirm}
        onDismiss={handleAlertDismiss}
        testID={ConfirmationAlertTestID.LeaveBooking}
        title="Are you sure?"
      />
    </>
  );
};

// eslint-disable-next-line import-x/no-unused-modules -- Modal/Tab/TabLayout/Screen/Home get used in expo routing but in a way that TypeScript can not infer
export default Modal;
